import React, { useRef, useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { defaultAll } from '@/constants/list';
import { DEFAULT_LANGUAGE } from '@/constants';
import { useSelector } from 'react-redux';
import { useParams } from 'next/navigation';
import Icon from '../Icon';
import CategoryChip from './components/CategoryChip';

function SubCategoriesFilter({
  subCatMenu,
  openMenu,
  handleFilterProducts,
  handleMenuOpen,
  activeItem,
  isCategory = true,
}) {
  const { slug } = useParams();
  const { lang } = useSelector((state) => state.globalSettings);
  const { currentChildren, current } = useSelector((state) => state.categories);
  const { filters } = useSelector((state) => state.products);
  const listRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Check if the category list is overflowing
  useEffect(() => {
    const checkOverflow = () => {
      if (listRef.current) {
        const isContentOverflowing =
          listRef.current.scrollWidth > listRef.current.clientWidth;
        setIsOverflowing(isContentOverflowing);
      }
    };

    // Check initially
    checkOverflow();

    // Check on window resize
    window.addEventListener('resize', checkOverflow);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [currentChildren]);

  const renderChildren = () => (
    <>
      {/* for default All */}
      <li
        role="presentation"
        onClick={(e) => {
          // reduce api call if click same button multiply times
          if (
            (filters?.category === slug && isCategory) ||
            (filters?.brand === slug && !isCategory && filters?.category === '')
          ) {
            return null;
          }

          handleFilterProducts({
            e,
            category: isCategory ? current?.slug : null,
          });
        }}
      >
        <CategoryChip
          activeSlug={activeItem}
          slug={isCategory ? current?.slug : defaultAll?.slug}
          name={defaultAll?.name}
        />
      </li>

      {/* children list */}
      {currentChildren.length > 0
        ? currentChildren?.map((item, index) => {
            const isLastItem = index === currentChildren.length - 1;
            return (
              <li
                role="presentation"
                key={item.id}
                onClick={(e) =>
                  handleFilterProducts({
                    e,
                    category: current.slug,
                    subCategorySlug: item.slug,
                  })
                }
              >
                <CategoryChip
                  activeSlug={activeItem}
                  slug={item?.slug}
                  name={item?.name[lang] ?? item?.name[DEFAULT_LANGUAGE]}
                  isLastItem={isLastItem}
                />
              </li>
            );
          })
        : null}
    </>
  );

  return (
    <>
      {subCatMenu ? (
        <section
          className={cn(
            'sub-categories-filter bg-titanwhite mb-[12px] lg:mb-[20px] py-6 lg:py-8 shadow-trending-categories-search relative z-[100]',
            {
              'absolute z-[198] w-full': openMenu,
            },
          )}
        >
          <div className="container mx-auto relative transition-all duration-300 ease-in-out">
            <ul
              ref={listRef}
              className={cn(
                'flex items-center gap-2 w-auto h-auto overflow-x-auto scrollbar-hidden transition-all duration-1000',
                {
                  'max-h-max lg:flex-wrap lg:overflow-hidden': openMenu,
                  'max-h-[50px]': !openMenu,
                },
              )}
            >
              {renderChildren()}

              {/* expand icon - only show when content is overflowing or menu is open */}
              {(isOverflowing || openMenu) && (
                <li className="absolute -top-[5px] py-8 pl-24 pr-0 right-[15px] md:right-[39px] xl:right-[-2px] bg-category-gradient z-[100]">
                  <div
                    className={cn(
                      'size-[22px] lg:size-[32px] flex justify-center items-center rounded-[6px]',
                      {
                        'bg-white shadow-trending-categories-search': openMenu,
                      },
                    )}
                  >
                    <Icon
                      type="angle-down"
                      className={cn(
                        '!align-top transition duration-200 cursor-pointer',
                        {
                          '!rotate-180': openMenu,
                        },
                      )}
                      onClick={handleMenuOpen}
                    />
                  </div>
                </li>
              )}

              <div className="block lg:hidden w-[25px] bg-category-gradient py-20 px-[26px] absolute -top-[5px] right-[9px] z-[99]" />
            </ul>
          </div>
        </section>
      ) : null}
      {/* popover list in mobile */}
      <div
        className={cn(
          'block lg:hidden mx-16 absolute top-0 left-0 right-0 opacity-0 translate-y-[20px] transition-all duration-200 ease-in-out',
          {
            'opacity-100 translate-y-[38px] z-[99] block': openMenu,
            'opacity-0 translate-y-[20px] z-[-1]': !openMenu,
          },
        )}
      >
        <ul className="flex flex-wrap items-center gap-[4px] bg-titanwhite shadow-category-menu p-16 rounded-b-[12px]">
          {renderChildren()}
        </ul>
      </div>
    </>
  );
}

export default SubCategoriesFilter;
